/**
 * The `authRedirect` query param: where the auth flow sends the user once they
 * are signed in.
 *
 * Three places plant it (the consent guard, the incomplete-onboarding guard and
 * the consent-error handler) and one place replays it
 * (useAuthSummary.redirectAfterAuthSuccess). All of them have to agree on one
 * rule: an auth-flow path may never become the redirect target. Otherwise a
 * visitor who entered the flow at `/auth/login` is sent back to `/auth/login`
 * after signing up, sees the login screen of the account they just created and
 * only escapes when that view's own mounted hook gives up and replaces `/`.
 */

// `/auth`, its children, and `/onboarding`. Anchored, so it can never match a
// protocol-relative path.
const AUTH_FLOW_PATH_RE = /^\/(auth|onboarding)(\/|$)/

/**
 * @param {String} [path] - a route path
 * @returns {Boolean} whether it points into the auth flow
 */
export const isAuthFlowPath = path =>
  typeof path === 'string' && AUTH_FLOW_PATH_RE.test(path)

/**
 * The `authRedirect` part of a guard's redirect query. Empty when the path being
 * left is itself in the auth flow, so `{ ...to.query }` spreads add nothing.
 *
 * @param {String} [fullPath] - the route the user was heading for
 * @returns {Object} `{ authRedirect }`, or `{}` when there is nothing to return to
 */
export function authRedirectQuery(fullPath) {
  if (!fullPath || isAuthFlowPath(fullPath)) return {}
  return { authRedirect: fullPath }
}

/**
 * Where to send a user who just authenticated.
 *
 * @param {String} [authRedirect] - the route query value, which reaches us from
 *   the guards above but also straight from a hand-edited URL
 * @returns {String|null} the path to replay, or null to fall back to the app root
 */
export function postAuthRedirectPath(authRedirect) {
  if (typeof authRedirect !== 'string' || !authRedirect) return null
  if (isAuthFlowPath(authRedirect)) return null
  // only in-app destinations: an absolute or protocol-relative URL here would
  // turn the auth flow into an open redirect
  if (!authRedirect.startsWith('/') || authRedirect.startsWith('//')) return null
  return authRedirect
}

const STASH_KEY = 'authRedirect:stash'
const STASH_TTL = 48 * 60 * 60 * 1000 // recovery token lifetime (auth service)

/**
 * Persist the redirect target across flows that leave the app for email
 * (password recovery), where the URL query cannot carry it.
 *
 * @param {String} [authRedirect] - the route query value
 */
export function stashAuthRedirect(authRedirect) {
  const path = postAuthRedirectPath(authRedirect)
  if (!path) return
  try {
    localStorage.setItem(STASH_KEY, JSON.stringify({ path, expires: Date.now() + STASH_TTL }))
  } catch {
    // storage unavailable — the flow degrades to losing the redirect, as before
  }
}

/**
 * Consume the stashed redirect. Validated like the query param, so a
 * hand-edited storage value gets the same open-redirect treatment.
 *
 * @returns {String|null} the path to replay, at most once
 */
export function takeStashedAuthRedirect() {
  try {
    const raw = localStorage.getItem(STASH_KEY)
    if (!raw) return null
    localStorage.removeItem(STASH_KEY)
    const { path, expires } = JSON.parse(raw)
    if (!expires || expires < Date.now()) return null
    return postAuthRedirectPath(path)
  } catch {
    return null
  }
}
